import React, { useEffect, useState } from 'react';
import api from '../api';

const fmtWhen = (t) => (t ? new Date(t).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '—');

// 0.45 reads as "45p", 1.2 as "£1.20". Whatever the server says the rate is.
const fmtRate = (rate) => {
  if (rate === null) return 'the club\'s mileage rate';
  if (rate >= 1) return `£${rate.toFixed(2)} a mile`;
  const pence = rate * 100;
  return `${pence.toFixed(Number.isInteger(pence) ? 0 : 2)}p a mile`;
};

const Venues = () => {
  const [venues, setVenues] = useState([]);
  const [status, setStatus] = useState({ auto_available: false, home_court: null });
  // Read from the server, never written into this file — see CoachHome.
  const [mileageRate, setMileageRate] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', round_trip_miles: '' });
  const [editing, setEditing] = useState(null); // { id, miles }
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [v, s, r] = await Promise.all([
      api.get('/api/venues'),
      api.get('/api/venues/mileage-status'),
      api.get('/api/venues/mileage-rate')
    ]);
    setVenues(v.data);
    setStatus(s.data);
    const rate = Number(r.data.mileage_rate_per_mile);
    setMileageRate(Number.isFinite(rate) ? rate : null);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/api/venues', {
        name: form.name,
        address: form.address,
        round_trip_miles: form.round_trip_miles === '' ? null : Number(form.round_trip_miles)
      });
      setNotice(res.data.warning || `${res.data.name} added${res.data.round_trip_miles != null ? ` at ${res.data.round_trip_miles} miles round trip` : ''}.`);
      setForm({ name: '', address: '', round_trip_miles: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add venue');
    } finally {
      setBusy(false);
    }
  };

  const saveMiles = async (id) => {
    setError(''); setNotice('');
    try {
      const res = await api.patch(`/api/venues/${id}`, {
        round_trip_miles: editing.miles === '' ? null : Number(editing.miles)
      });
      setNotice(res.data.warning || (editing.miles === ''
        ? 'Override cleared — back to the automatic figure.'
        : `Mileage set to ${res.data.round_trip_miles} miles and it will not be overwritten.`));
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the mileage');
    }
  };

  const recalculate = async (id) => {
    setError(''); setNotice('');
    try {
      const res = await api.post(`/api/venues/${id}/recalculate`);
      setNotice(`${res.data.name}: ${res.data.round_trip_miles} miles round trip.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not work the mileage out');
    }
  };

  return (
    <>
      <h1>Venues</h1>
      <div className="card">
        <h2>Add an away venue</h2>
        <p className="muted">
          Mileage is a round trip from the home court, the same figure for every coach, paid at {fmtRate(mileageRate)}.
          Coaches never type mileage — it comes from here.
        </p>
        {status.home_court ? (
          <p className="muted">Home court: <strong>{status.home_court.name}</strong>{status.home_court.address ? `, ${status.home_court.address}` : ''}.</p>
        ) : (
          <p className="error-msg">No home court is set, so mileage cannot be worked out.</p>
        )}
        <p className="muted">
          {status.auto_available
            ? 'Automatic mileage is switched on. Enter the address and leave the miles blank to have it worked out for you, or type a figure to override it.'
            : 'Automatic mileage is switched off (no routing key set yet), so type the round-trip mileage in yourself.'}
        </p>
        {error && <div className="error-msg">{error}</div>}
        {notice && <div className="success-msg">{notice}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-row">
              <label>Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-row">
              <label>Address</label>
              <input value={form.address} placeholder="Street, town, postcode"
                     onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Round-trip miles {status.auto_available ? '(leave blank to work out automatically)' : ''}</label>
              <input type="number" step="0.1" min="0" value={form.round_trip_miles}
                     onChange={e => setForm({ ...form, round_trip_miles: e.target.value })} />
            </div>
          </div>
          <button className="btn" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add venue'}</button>
        </form>
      </div>

      <div className="card">
        <h2>All venues</h2>
        <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Name</th><th>Address</th><th>Type</th><th>Round-trip miles</th><th>Where the figure came from</th><th></th></tr>
          </thead>
          <tbody>
            {venues.map(v => (
              <tr key={v.id}>
                <td>{v.name}</td>
                <td>{v.address || '—'}</td>
                <td>{v.is_home_court ? 'Home court' : 'Away'}</td>
                <td>
                  {v.is_home_court ? '—' : (
                    editing && editing.id === v.id ? (
                      <input type="number" step="0.1" min="0" value={editing.miles} style={{ width: '6rem' }}
                             onChange={e => setEditing({ ...editing, miles: e.target.value })} />
                    ) : (v.round_trip_miles ?? 'not set')
                  )}
                </td>
                <td className="muted">
                  {v.is_home_court ? 'Journeys are measured from here' : (
                    v.miles_source === 'manual'
                      ? `Typed in by an admin on ${fmtWhen(v.miles_calculated_at)} — kept through recalculations`
                      : v.miles_source === 'auto'
                        ? `${v.miles_note || 'Worked out automatically'} (${fmtWhen(v.miles_calculated_at)})`
                        : 'Not set yet'
                  )}
                </td>
                <td>
                  {!v.is_home_court && (
                    editing && editing.id === v.id ? (
                      <>
                        <button className="btn small" onClick={() => saveMiles(v.id)}>Save</button>{' '}
                        <button className="btn small secondary" onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn small secondary"
                                onClick={() => setEditing({ id: v.id, miles: v.round_trip_miles ?? '' })}>
                          {v.miles_source === 'manual' ? 'Edit / clear override' : 'Override'}
                        </button>{' '}
                        {status.auto_available && v.miles_source !== 'manual' && (
                          <button className="btn small secondary" onClick={() => recalculate(v.id)}>Recalculate</button>
                        )}
                      </>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="muted">
          To go back to the automatic figure on a venue you have overridden, clear the miles box and save.
        </p>
      </div>
    </>
  );
};

export default Venues;
